import React from "react";
import { Button, Form, Spinner, Row, Col, Badge, Card } from "react-bootstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import dayjs from "dayjs";
import { Info, FileText, ChevronDown, ChevronUp } from "react-feather";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import { questionDifficultyLevels } from "constants/Constants";

const BLOOM_TAXONOMY = ["Remember", "Understand", "Apply", "Analyze", "Evaluate", "Create"];

const QuizGenerationForm = ({
  questionConfigs,
  setQuestionConfigs,
  orgConfig,
  setOrgConfig,
  handleOnSubmit,
  totalQuestions,
  isGenerating,
  quizTimer,
  setQuizTimer,
  vectorisedResources = [],
  isLoadingResources = false,
  scheduledAttempt,
  setScheduledAttempt,
  startDate,
  setStartDate,
  startTime,
  setStartTime,
  endDate,
  setEndDate,
  endTime,
  setEndTime,
  passingPercentage,
  setPassingPercentage,
  finalAssessment,
  setFinalAssessment,
  isProctored,
  setIsProctored,
  maxAttempts,
  setMaxAttempts,
  allowMultipleAttempts,
  setAllowMultipleAttempts,
  learningOutcomes = [],
}) => {
  const [expandedOutcomes, setExpandedOutcomes] = React.useState({});

  const toggleOutcomes = (type) => {
    setExpandedOutcomes(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const updateConfig = (type, field, value) => {
    setQuestionConfigs(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: value }
    }));
  };

  const updateOrgConfig = (field, value) => {
    setOrgConfig(prev => ({ ...prev, [field]: value }));
  };

  const updateOutcomeDistribution = (type, co, value) => {
    setQuestionConfigs(prev => {
      const outcomes = { ...(prev[type].outcomes || {}) };
      if (value === 0 || value === "") {
        delete outcomes[co];
      } else {
        outcomes[co] = parseInt(value) || 0;
      }
      return {
        ...prev,
        [type]: { ...prev[type], outcomes }
      };
    });
  };

  const renderTypeConfig = (type, label) => {
    const config = questionConfigs[type];
    if (!config) return null;

    return (
      <Card className="mb-3 border-light shadow-none" key={type}>
        <Card.Body className="p-3">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <Form.Check
              type="checkbox"
              id={`check-${type}`}
              label={<span className="fw-bold fs-5 text-dark ms-2">{label}</span>}
              checked={config.enabled}
              onChange={(e) => updateConfig(type, 'enabled', e.target.checked)}
            />
            {config.enabled && <Badge bg="light" text="primary" className="border border-primary border-opacity-25 px-2 py-1">{config.count} Questions</Badge>}
          </div>

          {config.enabled && (
            <div className="ps-4 pt-2 border-start border-2 border-primary border-opacity-10">
              <Row className="g-3">
                <Col md={3}>
                  <Form.Label className="small text-muted fw-bold">COUNT</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Button variant="outline-secondary" size="sm" onClick={() => updateConfig(type, 'count', Math.max(0, config.count - 1))}>-</Button>
                    <Form.Control
                      type="number"
                      size="sm"
                      className="text-center fw-bold w-50"
                      value={config.count}
                      onChange={(e) => updateConfig(type, 'count', parseInt(e.target.value) || 0)}
                    />
                    <Button variant="outline-secondary" size="sm" onClick={() => updateConfig(type, 'count', Math.min(20, config.count + 1))}>+</Button>
                  </div>
                </Col>
                <Col md={3}>
                  <Form.Label className="small text-muted fw-bold">MARKS</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    value={config.marks}
                    onChange={(e) => updateConfig(type, 'marks', parseInt(e.target.value) || 0)}
                  />
                </Col>
                {/* <Col md={3}>
                  <Form.Label className="small text-muted fw-bold">DIFFICULTY</Form.Label>
                  <Form.Select
                    size="sm"
                    value={config.difficulty}
                    onChange={(e) => updateConfig(type, 'difficulty', e.target.value)}
                  >
                    {questionDifficultyLevels.map(lvl => (
                      <option key={lvl.value} value={lvl.value}>{lvl.label}</option>
                    ))}
                  </Form.Select>
                </Col> */}
                <Col md={3}>
                  <Form.Label className="small text-muted fw-bold">TAXONOMY</Form.Label>
                  <Form.Select
                    size="sm"
                    value={config.taxonomy}
                    onChange={(e) => updateConfig(type, 'taxonomy', e.target.value)}
                  >
                    {BLOOM_TAXONOMY.map(tax => (
                      <option key={tax} value={tax}>{tax}</option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>

              {/* Course Outcome Distribution Section */}
              {learningOutcomes.length > 0 && (
                <div className="mt-4 pt-3 border-top border-dashed">
                  <div
                    className="d-flex align-items-center justify-content-between mb-3 cursor-pointer"
                    onClick={() => toggleOutcomes(type)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <h6 className="text-primary fw-bold mb-0">COURSE OUTCOME DISTRIBUTION</h6>
                      <div className="text-muted">
                        {expandedOutcomes[type] === false ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                      </div>
                    </div>
                    <Badge
                      bg={Object.values(config.outcomes || {}).reduce((a, b) => a + b, 0) === config.count ? "success" : "warning"}
                      className="opacity-75"
                    >
                      {Object.values(config.outcomes || {}).reduce((a, b) => a + b, 0)} / {config.count} Assigned
                    </Badge>
                  </div>

                  {expandedOutcomes[type] !== false && (
                    <>
                      <div className="d-flex flex-column gap-2">
                        {learningOutcomes.map((co, idx) => (
                          <div key={idx} className="d-flex align-items-center bg-white p-2 rounded border shadow-sm" style={{ minWidth: 0 }}>
                            <div className="flex-grow-1 min-w-0 me-3">
                              <div className="small text-dark fw-medium text-truncate" title={co}>
                                {co}
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-1 flex-shrink-0">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="p-0 d-flex align-items-center justify-content-center"
                                style={{ width: '28px', height: '28px', borderRadius: '4px' }}
                                onClick={() => updateOutcomeDistribution(type, co, Math.max(0, (config.outcomes?.[co] || 0) - 1))}
                              >
                                -
                              </Button>
                              <Form.Control
                                type="number"
                                size="sm"
                                style={{ width: '45px', textAlign: 'center', fontWeight: 'bold' }}
                                className="bg-light border-0 px-1"
                                value={config.outcomes?.[co] || 0}
                                onChange={(e) => updateOutcomeDistribution(type, co, e.target.value)}
                                onWheel={(e) => e.target.blur()}
                                min="0"
                                max={config.count}
                              />
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="p-0 d-flex align-items-center justify-content-center"
                                style={{ width: '28px', height: '28px', borderRadius: '4px' }}
                                onClick={() => updateOutcomeDistribution(type, co, Math.min(config.count, (config.outcomes?.[co] || 0) + 1))}
                              >
                                +
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                      {Object.values(config.outcomes || {}).reduce((a, b) => a + b, 0) !== config.count && (
                        <p className="text-warning x-small mt-2 mb-0">
                          <Info size={12} className="me-1" />
                          Total assigned questions should match the count ({config.count}).
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <div className="quiz-generation-workflow">
      {/* 1. Resource Selection */}
      <Card className="mb-4 border-light shadow-none">
        <Card.Body className="p-4">
          <div className="d-flex align-items-center gap-2 mb-3">
            <FileText className="text-primary" size={20} />
            <h5 className="mb-0 fw-bold">Resource Selection</h5>
          </div>

          {/* New: Domain and Department Row */}
          <Row className="g-3 mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="form-label fw-medium">Domain</Form.Label>
                <Form.Control
                  type="text"
                  value={orgConfig.selectedDomain || "Not specified"}
                  readOnly
                  className="bg-light border-0 py-2"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="form-label fw-medium">Department</Form.Label>
                <Form.Control
                  type="text"
                  value={orgConfig.selectedDepartment || "Not specified"}
                  readOnly
                  className="bg-light border-0 py-2"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-0 position-relative">
            <Form.Label className="form-label fw-medium">Resource <span className="text-danger">*</span></Form.Label>
            <div className="d-flex align-items-center">
              <Form.Select
                value={orgConfig.vectorisedFile || ""}
                onChange={(e) => updateOrgConfig('vectorisedFile', e.target.value)}
                className="py-2 text-dark"
                disabled={isLoadingResources}
              >
                {isLoadingResources ? (
                  <option>Loading resources...</option>
                ) : (
                  <>
                    <option value="">Select a resource to generate questions from...</option>
                    {(vectorisedResources || []).flatMap(res => [
                      ...(res.videoContents || []),
                      ...(res.supplementaryMaterials || [])
                    ]).filter(v => v.vectorSpace).map(v => (
                      <option key={v.contentFileName} value={v.contentFileName}>
                        {v.contentFileName.split('.').slice(0, -1).join('.')}
                      </option>
                    ))}
                  </>
                )}
              </Form.Select>
              {isLoadingResources && (
                <Spinner animation="border" size="sm" className="ms-2 text-primary" />
              )}
            </div>
            {!orgConfig.vectorisedFile && !isLoadingResources && (
              <Form.Text className="text-warning small mt-1 d-block">
                Required for generation.
              </Form.Text>
            )}
          </Form.Group>
        </Card.Body>
      </Card>

      {/* 2. Question Types */}
      <div className="mb-4">
        <div className="d-flex align-items-center justify-content-between mb-4 border-bottom py-2">
          <h4 className="mb-0">Question Types</h4>
          <Badge bg="primary" pill>{totalQuestions} Total Questions</Badge>
        </div>
        {renderTypeConfig('mcq', 'Multiple Choice Questions')}
        {renderTypeConfig('fill_in_the_blanks', 'Fill in the Blanks')}
        {renderTypeConfig('true_or_false', 'True / False')}
        {renderTypeConfig('very_short_answer', 'Very Short Answer')}
        {renderTypeConfig('short_answer', 'Short Answer')}
        {renderTypeConfig('medium_answer', 'Medium Answer')}
        {renderTypeConfig('long_answer', 'Long Answer')}
        {renderTypeConfig('essay_answer', 'Essay Answer')}
      </div>

      {/* 3. Quiz Metadata */}
      <Card className="mb-4 border-light shadow-none">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4 border-bottom py-2">
            <h4 className="mb-0">Quiz Metadata</h4>
          </div>
          <Row className="g-3">
            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium">Passing Percentage</label>
                <Form.Control
                  type="number"
                  id="passing-percentage"
                  placeholder="Enter passing percentage"
                  name="passingPercentage"
                  value={passingPercentage}
                  onChange={(e) => setPassingPercentage(e.target.value > 100 ? 100 : e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  className="text-dark"
                  min="0"
                  max="100"
                />
              </div>
            </Col>

            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium d-flex align-items-center">
                  Final Assessment
                  {allowMultipleAttempts && (
                    <Tippy content="Not applicable when Allow Multiple Attempts is enabled — a final assessment is a one-shot evaluation.">
                      <span className="ms-2">
                        <Info size={16} className="text-muted" />
                      </span>
                    </Tippy>
                  )}
                </label>
                <div className="mt-2">
                  <Form.Check
                    name="finalAssessment"
                    onChange={(e) => setFinalAssessment(e.target.checked)}
                    checked={!allowMultipleAttempts && finalAssessment}
                    disabled={allowMultipleAttempts}
                    label="Mark as final assessment"
                    id="final-assessment-check"
                  />
                </div>
              </div>
            </Col>

            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium">Quiz Timer (Minutes)</label>
                <Form.Control
                  type="number"
                  id="quiz-timer"
                  placeholder="Enter quiz duration"
                  name="quizTimer"
                  value={quizTimer}
                  onChange={(e) => setQuizTimer(Number(e.target.value))}
                  onWheel={(e) => e.target.blur()}
                  className="text-dark"
                  min="1"
                />
              </div>
            </Col>

            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium d-flex align-items-center">
                  Allow Multiple Attempts
                  <Tippy content={finalAssessment ? "Not applicable when Final Assessment is enabled." : "Not applicable when Scheduled or Proctored Quiz is enabled."}>
                    <span className="ms-2">
                      <Info size={16} className="text-muted" />
                    </span>
                  </Tippy>
                </label>
                <div className="mt-2">
                  <Form.Check
                    type="switch"
                    id="allow-multiple-attempts-switch"
                    checked={(scheduledAttempt || isProctored || finalAssessment) ? false : allowMultipleAttempts}
                    onChange={(e) => setAllowMultipleAttempts(e.target.checked)}
                    disabled={scheduledAttempt || isProctored || finalAssessment}
                    label={(scheduledAttempt || isProctored || finalAssessment) || !allowMultipleAttempts ? "No Attempt Tracking" : "Multiple Attempts"}
                  />
                </div>
              </div>
            </Col>

            {allowMultipleAttempts && !(scheduledAttempt || isProctored || finalAssessment) && (
              <Col xs={12} md={6} lg={4}>
                <div className="mb-3">
                  <label className="form-label fw-medium">Max Attempts</label>
                  <Form.Control
                    type="number"
                    id="max-attempts"
                    placeholder="Enter max attempts allowed"
                    name="maxAttempts"
                    value={maxAttempts}
                    onChange={(e) => setMaxAttempts(e.target.value)}
                    onWheel={(e) => e.target.blur()}
                    className="text-dark"
                    min="2"
                  />
                </div>
              </Col>
            )}

            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium d-flex align-items-center">
                  Scheduled Quiz
                  <Tippy content="If enabled, students can only take the quiz within the specified window.">
                    <span className="ms-2">
                      <Info size={16} className="text-muted" />
                    </span>
                  </Tippy>
                </label>
                <div className="mt-2">
                  <Form.Check
                    type="switch"
                    id="scheduled-quiz-switch"
                    checked={scheduledAttempt}
                    onChange={(e) => setScheduledAttempt(e.target.checked)}
                    label={scheduledAttempt ? "Scheduled" : "Always Available"}
                  />
                </div>
              </div>
            </Col>

            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium d-flex align-items-center">
                  Proctored Quiz
                  <Tippy content="Enabling proctoring will apply Fullscreen Enforcement, Tab/Window Switch Detection, Copy-Paste & Right-Click Disable, and DevTools/Shortcut Blocking when attempting this quiz.">
                    <span className="ms-2">
                      <Info size={16} className="text-muted" />
                    </span>
                  </Tippy>
                </label>
                <div className="mt-2">
                  <Form.Check
                    type="switch"
                    id="proctoring-switch"
                    checked={isProctored}
                    onChange={(e) => setIsProctored(e.target.checked)}
                    label={isProctored ? "Proctoring Enabled" : "Proctoring Disabled"}
                  />
                </div>
              </div>
            </Col>

            {scheduledAttempt && (
              <>
                <Col xs={12} md={6}>
                  <div className="mb-3">
                    <label className="form-label fw-medium text-primary">Access Opens (Start)</label>
                    <Row className="g-2">
                      <Col>
                        <Flatpickr
                          value={startDate}
                          onChange={([date]) => setStartDate(date ? dayjs(date).format("YYYY-MM-DD") : "")}
                          className="form-control text-dark"
                          placeholder="Select Start Date"
                          options={{ dateFormat: "Y-m-d" }}
                        />
                      </Col>
                      <Col>
                        <MobileTimePicker
                          value={startTime ? dayjs(startTime, "HH:mm") : null}
                          onChange={(v) => setStartTime(v ? v.format("HH:mm") : "")}
                          views={["hours", "minutes"]}
                          openTo="hours"
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              className: "bg-white",
                              sx: { "& .MuiInputBase-root": { height: "38px" } },
                            },
                          }}
                        />
                      </Col>
                    </Row>
                  </div>
                </Col>

                <Col xs={12} md={6}>
                  <div className="mb-3">
                    <label className="form-label fw-medium text-danger">Access Closes (End)</label>
                    <Row className="g-2">
                      <Col>
                        <Flatpickr
                          value={endDate}
                          onChange={([date]) => setEndDate(date ? dayjs(date).format("YYYY-MM-DD") : "")}
                          className="form-control text-dark"
                          placeholder="Select End Date"
                          options={{ dateFormat: "Y-m-d" }}
                        />
                      </Col>
                      <Col>
                        <MobileTimePicker
                          value={endTime ? dayjs(endTime, "HH:mm") : null}
                          onChange={(v) => setEndTime(v ? v.format("HH:mm") : "")}
                          views={["hours", "minutes"]}
                          openTo="hours"
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              className: "bg-white",
                              sx: { "& .MuiInputBase-root": { height: "38px" } },
                            },
                          }}
                        />
                      </Col>
                    </Row>
                  </div>
                </Col>
              </>
            )}
          </Row>
        </Card.Body>
      </Card>

      <div className="d-grid gap-2">
        <Button
          variant="primary"
          size="lg"
          disabled={isGenerating || totalQuestions === 0 || !orgConfig.vectorisedFile}
          onClick={handleOnSubmit}
          className="py-3 fw-bold"
        >
          {isGenerating ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Generating Questions and Answers...
            </>
          ) : (
            `Generate Question and Answers (${totalQuestions})`
          )}
        </Button>
        {isGenerating && <p className="text-center text-muted small mt-2">Questions and Answers are getting generated, please wait and do not refresh the page</p>}
      </div>
    </div>
  );
};

export default QuizGenerationForm;